/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Link, Shadows, Typography } from '@zapier/design-system';

import type { UniversalNavLinkType } from '../../../constants';

type Props = {
  /**
   * Array of links to render within dropdown menu.
   */
  links: Array<UniversalNavLinkType>;
};

const Styles = labelStyles('Submenu', {
  root: css`
    background-color: ${Colors.white};
    border-radius: 0 0 10px 10px;
    padding: 20px;
    box-shadow: ${Shadows.elevation20};
  `,

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
  `,

  link: css`
    ${Typography.base};
    display: block;
    border-radius: 10px;
    color: ${Colors.gray};
    line-height: 20px;
    font-size: 14px;
    padding: 10px;
    text-decoration: none;

    &:hover,
    &:focus {
      color: ${Colors.ocean};
      background-color: ${Colors.ocean5};
      outline: none;
    }
  `,

  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: css`
    display: block;
    font-weight: 700;
  `,

  linkDescription: css`
    display: block;
    margin-top: 5px;
    color: ${Colors.gray80};
    font-size: 12px;
    line-height: 15px;
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  return (
    <Link color={null} css={Styles.link} href={props.href} role="menuitem">
      <div css={Styles.linkInner}>
        <p css={Styles.linkTitle}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription}>{props.description}</p>
        )}
      </div>
    </Link>
  );
};

/**
 * @private
 * Submenu component acts as a dropdown on universal nav links
 */
const Submenu = (props: Props) => {
  const linkItems = props.links.map((link) => {
    // We set role="none" here in combination with role="menuitem" above on
    // the Link element for accessibility, given role="menu" in the ul below.
    // https://www.w3.org/TR/wai-aria-practices/examples/menu-button/menu-button-links.html
    return (
      <li key={link.text} role="none">
        <SubmenuLink {...link} />
      </li>
    );
  });
  return (
    <div css={Styles.root}>
      <ul css={Styles.list} role="menu">
        {linkItems}
      </ul>
    </div>
  );
};

export default Submenu;
